import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import MultiEntryField from '@/components/MultiEntryField';
import AffiliationField from '@/components/AffiliationField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  User, 
  Briefcase, 
  Building2, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Camera,
  Trash2,
  Palette,
  Save,
  ArrowLeft,
  Linkedin,
  Twitter,
  Instagram,
  Github,
  Facebook
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_PLATFORMS = [
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/username' },
  { id: 'twitter', label: 'Twitter', icon: Twitter, placeholder: 'https://twitter.com/username' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/username' },
  { id: 'github', label: 'GitHub', icon: Github, placeholder: 'https://github.com/username' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/username' },
];

const CreateCardPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    profile_photo: '',
    qr_color: '#2563EB',
    qr_bg_color: '#FFFFFF',
    social_links: [],
    // Affiliations
    affiliations: [],
    // Personal contact info
    personal_emails: [],
    personal_phones: [],
    personal_websites: [],
    personal_addresses: [],
    // Legacy fields
    company: '',
    job_titles: [],
    emails: [],
    phones: [],
    websites: [],
    addresses: []
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload/photo`, formDataUpload, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      handleChange('profile_photo', response.data.url);
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const addSocialLink = (platform) => {
    const existing = formData.social_links.find(l => l.platform === platform);
    if (existing) {
      toast.error(`${platform} already added`);
      return;
    }
    setFormData(prev => ({
      ...prev,
      social_links: [...prev.social_links, { platform, url: '' }]
    }));
  };

  const updateSocialLink = (platform, url) => {
    setFormData(prev => ({
      ...prev,
      social_links: prev.social_links.map(link => 
        link.platform === platform ? { ...link, url } : link
      )
    }));
  };

  const removeSocialLink = (platform) => {
    setFormData(prev => ({
      ...prev,
      social_links: prev.social_links.filter(link => link.platform !== platform)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/cards`, formData, { headers: getAuthHeaders() });
      toast.success('Card created successfully!');
      navigate(`/cards/${response.data.id}/edit`);
    } catch (error) {
      toast.error('Failed to create card');
    } finally {
      setLoading(false);
    }
  };

  // Get primary values for preview
  const primaryJobTitle = formData.job_titles[0]?.value || '';
  const primaryEmail = formData.emails[0]?.value || '';
  const primaryPhone = formData.phones[0]?.value || '';
  const primaryWebsite = formData.websites[0]?.value || '';

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Create New Card</h1>
            <p className="text-muted-foreground">Fill in your details to create a digital business card</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="split-view">
            {/* Form Section */}
            <div className="space-y-6">
              {/* Profile Photo */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Camera className="w-5 h-5" />
                    Profile Photo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div 
                      className="photo-upload"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="photo-upload-btn"
                    >
                      {formData.profile_photo ? (
                        <img src={formData.profile_photo} alt="Profile" />
                      ) : (
                        <Camera className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium">Upload a photo</p>
                      <p className="text-xs text-muted-foreground">JPG, PNG or WebP. Max 5MB.</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Basic Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      data-testid="name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      placeholder="A short bio about yourself..."
                      rows={3}
                      value={formData.bio}
                      onChange={(e) => handleChange('bio', e.target.value)}
                      data-testid="bio-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Company Affiliations */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Company Affiliations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AffiliationField
                    affiliations={formData.affiliations}
                    onChange={(affiliations) => handleChange('affiliations', affiliations)}
                    maxAffiliations={5}
                  />
                </CardContent>
              </Card>

              {/* Personal Contact Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Personal Contact Information
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Contact details not tied to any specific company
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <MultiEntryField
                    field="personal_emails"
                    entries={formData.personal_emails}
                    onChange={(entries) => handleChange('personal_emails', entries)}
                    icon={Mail}
                    placeholder="personal@email.com"
                    inputType="email"
                    label="Personal Email Addresses"
                  />

                  <Separator />

                  <MultiEntryField
                    field="personal_phones"
                    entries={formData.personal_phones}
                    onChange={(entries) => handleChange('personal_phones', entries)}
                    icon={Phone}
                    placeholder="+1 234 567 8900"
                    inputType="tel"
                    label="Personal Phone Numbers"
                  />

                  <Separator />

                  <MultiEntryField
                    field="personal_websites"
                    entries={formData.personal_websites}
                    onChange={(entries) => handleChange('personal_websites', entries)}
                    icon={Globe}
                    placeholder="https://yourportfolio.com"
                    inputType="url"
                    label="Personal Websites"
                  />

                  <Separator />

                  <MultiEntryField
                    field="personal_addresses"
                    entries={formData.personal_addresses}
                    onChange={(entries) => handleChange('personal_addresses', entries)}
                    icon={MapPin}
                    placeholder="Home address"
                    isTextarea={true}
                    label="Personal Addresses"
                  />
                </CardContent>
              </Card>

              {/* Social Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Social Links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.social_links.map((link) => {
                    const platform = SOCIAL_PLATFORMS.find(p => p.id === link.platform);
                    const Icon = platform?.icon || Globe;
                    return (
                      <div key={link.platform} className="flex items-center gap-2">
                        <Icon className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        <Input
                          placeholder={platform?.placeholder}
                          value={link.url}
                          onChange={(e) => updateSocialLink(link.platform, e.target.value)}
                          data-testid={`social-${link.platform}-input`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeSocialLink(link.platform)}
                          data-testid={`remove-social-${link.platform}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    );
                  })}
                  
                  <Separator />
                  
                  <div className="flex flex-wrap gap-2">
                    {SOCIAL_PLATFORMS.filter(p => !formData.social_links.find(l => l.platform === p.id)).map((platform) => {
                      const Icon = platform.icon;
                      return (
                        <Button
                          key={platform.id}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addSocialLink(platform.id)}
                          data-testid={`add-social-${platform.id}`}
                        >
                          <Icon className="w-4 h-4 mr-2" />
                          {platform.label}
                        </Button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* QR Customization */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    QR Code Customization
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="qr_color">QR Color</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          id="qr_color"
                          value={formData.qr_color}
                          onChange={(e) => handleChange('qr_color', e.target.value)}
                          className="color-swatch"
                          data-testid="qr-color-input"
                        />
                        <Input
                          value={formData.qr_color}
                          onChange={(e) => handleChange('qr_color', e.target.value)}
                          className="w-24 font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="qr_bg_color">Background</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          id="qr_bg_color"
                          value={formData.qr_bg_color}
                          onChange={(e) => handleChange('qr_bg_color', e.target.value)}
                          className="color-swatch"
                          data-testid="qr-bg-color-input"
                        />
                        <Input
                          value={formData.qr_bg_color}
                          onChange={(e) => handleChange('qr_bg_color', e.target.value)}
                          className="w-24 font-mono text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Button 
                type="submit" 
                size="lg" 
                className="w-full btn-glow gap-2"
                disabled={loading}
                data-testid="save-card-btn"
              >
                {loading ? (
                  <div className="loading-spinner w-5 h-5" />
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Create Card
                  </>
                )}
              </Button>
            </div>

            {/* Preview Section */}
            <div className="hidden lg:block sticky top-8">
              <Card className="overflow-hidden">
                <CardHeader className="bg-muted/50">
                  <CardTitle className="text-lg">Live Preview</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="mobile-preview-container mx-auto overflow-y-auto">
                    <div className="p-6 space-y-6">
                      {/* Profile Section */}
                      <div className="text-center pt-8">
                        {formData.profile_photo ? (
                          <img 
                            src={formData.profile_photo} 
                            alt="Profile"
                            className="w-24 h-24 rounded-full mx-auto object-cover ring-4 ring-primary/20"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-full mx-auto bg-primary/10 flex items-center justify-center ring-4 ring-primary/20">
                            <span className="text-3xl font-bold text-primary">
                              {formData.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <h2 className="text-xl font-bold mt-4">{formData.name || 'Your Name'}</h2>
                        {primaryJobTitle && (
                          <p className="text-muted-foreground">{primaryJobTitle}</p>
                        )}
                        {formData.company && (
                          <p className="text-sm text-muted-foreground">{formData.company}</p>
                        )}
                        {formData.job_titles.length > 1 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            +{formData.job_titles.length - 1} more title(s)
                          </p>
                        )}
                      </div>

                      {/* Contact Buttons Preview */}
                      <div className="space-y-2">
                        {primaryEmail && (
                          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                            <Mail className="w-5 h-5 text-primary" />
                            <span className="text-sm truncate">{primaryEmail}</span>
                            {formData.emails.length > 1 && (
                              <span className="text-xs text-muted-foreground">+{formData.emails.length - 1}</span>
                            )}
                          </div>
                        )}
                        {primaryPhone && (
                          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                            <Phone className="w-5 h-5 text-primary" />
                            <span className="text-sm">{primaryPhone}</span>
                            {formData.phones.length > 1 && (
                              <span className="text-xs text-muted-foreground">+{formData.phones.length - 1}</span>
                            )}
                          </div>
                        )}
                        {primaryWebsite && (
                          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                            <Globe className="w-5 h-5 text-primary" />
                            <span className="text-sm truncate">{primaryWebsite}</span>
                            {formData.websites.length > 1 && (
                              <span className="text-xs text-muted-foreground">+{formData.websites.length - 1}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* QR Code Preview */}
                      <div className="flex justify-center">
                        <div className="qr-container">
                          <QRCodeSVG
                            value="https://cardlink.app/preview"
                            size={120}
                            fgColor={formData.qr_color}
                            bgColor={formData.qr_bg_color}
                            level="H"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateCardPage;
